import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import palette from '../../../components/styles/palette';
import { Text } from '../../../components/Base';
import {
  HIDE_SELECTED_ROUTE,
  SHOW_SELECTED_ROUTE,
} from '../../../constants/strings';
import { parseQuery, stringifyQuery } from '../../../utils/query';

import { setScrollToRoute } from '../../../store/reducers/routes';
import { setStopMarkerPopup } from '../../../store/reducers/map';

const useStyles = makeStyles(({ typography }) => ({
  button: {
    borderRadius: 4,
    border: `1px solid ${palette.typography.black.medium}`,
    padding: `${typography.pxToRem(5)} ${typography.pxToRem(10)}`,
    textTransform: 'capitalize',
    height: typography.pxToRem(30),
    fontSize: typography.pxToRem(14),
    boxSizing: 'border-box',
    boxShadow: '0px 3px 5px 1px rgba(0, 0, 0, 0.2)',
    zIndex: 2,
  },
  showButton: {
    position: 'absolute',
    top: typography.pxToRem(25),
    right: typography.pxToRem(20),
    background: palette.btn.blueFill,
    '&:hover': {
      background: palette.btn.blueFill,
    },
    '&.showButton $title': {
      color: palette.typography.white.solid,
    },
  },
  hideButton: {
    background: palette.snackbarButtons.yellow,
    '&:hover': {
      background: palette.snackbarButtons.yellow,
    },
  },
  title: {
    color: palette.typography.white.solid,
    fontSize: typography.pxToRem(14),
  },
  black: {
    color: palette.typography.black.solid,
  },
}));

const ToggleRouteDetailButton = ({ isDetailVisible }) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const title = useMemo(
    () => (isDetailVisible ? HIDE_SELECTED_ROUTE : SHOW_SELECTED_ROUTE),
    [isDetailVisible]
  );

  const onClick = useCallback(async () => {
    const query = parseQuery(location.search);
    if (!query.detailVisible) {
      dispatch(setStopMarkerPopup(null));
    }
    const newQuery = stringifyQuery({
      ...query,
      detailVisible: query.detailVisible ? undefined : true,
    });

    await navigate({ search: newQuery }, { replace: true });

    if (query.detailVisible) {
      dispatch(setScrollToRoute(query.routeId));
    }
  }, [location.search, navigate, dispatch]);

  return (
    <Button
      disableRipple
      disableFocusRipple
      variant="contained"
      onClick={onClick}
      className={clsx(
        classes.button,
        isDetailVisible ? classes.hideButton : classes.showButton
      )}
    >
      <Text
        noWrap
        className={clsx(classes.title, isDetailVisible && classes.black)}
      >
        {title}
      </Text>
    </Button>
  );
};

export default React.memo(ToggleRouteDetailButton);
